import { TroubleType } from '@erp/shared-types';
import type { useGT } from 'gt-react';
import { formatStoredLineTotalMismatchComment } from '@/components/accounts-payable/invoice-detail/lineTotalMismatch';

type Translate = ReturnType<typeof useGT>;

interface TroubleCommentEntry {
  type: string;
  comments?: readonly string[] | null;
}

const ORDER_NOT_READY_COMMENT =
  /^(SO|PO)\s+(\S+)\s+status=(\S+)\s+\(not ready for matching\)$/;

const INTERNAL_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Turn the machine comment stored on an ORDER_NOT_READY trouble entry into
 * the sentence shown on the invoice badge.
 *
 * Stored shape: `PO 45021 status=SENT (not ready for matching)`.
 * Older rows put the internal id in place of the order number; those ids are
 * left out of the sentence.
 */
export function formatOrderNotReadyComment(
  comment: string,
  gt: Translate,
): string {
  const match = ORDER_NOT_READY_COMMENT.exec(comment.trim());
  if (!match) return comment;

  const kind = match[1];
  const identifier = match[2];
  const statusCode = match[3];
  if (!kind || !identifier || statusCode === undefined) return comment;

  const orderNumber = INTERNAL_ID.test(identifier) ? null : identifier;
  const status =
    statusCode === 'unknown'
      ? null
      : kind === 'PO'
        ? purchaseOrderStatusLabel(statusCode, gt)
        : salesOrderStatusLabel(statusCode, gt);

  if (kind === 'PO') return purchaseOrderSentence(gt, orderNumber, status);
  return salesOrderSentence(gt, orderNumber, status);
}

export function orderNotReadyPausedMessage(gt: Translate): string {
  return gt('Matching is paused and will retry on its own.', {
    context:
      'Order Not Ready badge tooltip: matching will run again without a person',
  });
}

/** Shown when the badge has no order-specific comment to name. */
export function orderNotReadyResumeMessage(gt: Translate): string {
  return gt(
    'Matching resumes when the order reaches a status that allows matching.',
    {
      context: 'Order Not Ready badge tooltip when no specific order is listed',
    },
  );
}

/**
 * Lines under the badge label. An Order Not Ready entry with no stored
 * comment still explains when matching resumes.
 */
export function visibleTroubleComments(
  entry: TroubleCommentEntry,
  gt: Translate,
): string[] {
  const comments = entry.comments ?? [];
  if (entry.type === TroubleType.LINE_TOTAL_MISMATCH) {
    if (comments.length === 0) {
      return [formatStoredLineTotalMismatchComment('', gt)];
    }
    return comments.map((comment) =>
      formatStoredLineTotalMismatchComment(comment, gt),
    );
  }
  if (entry.type !== TroubleType.ORDER_NOT_READY) return [...comments];
  if (comments.length === 0) return [orderNotReadyResumeMessage(gt)];
  return comments.map((comment) => formatOrderNotReadyComment(comment, gt));
}

function purchaseOrderSentence(
  gt: Translate,
  orderNumber: string | null,
  status: string | null,
): string {
  if (orderNumber && status) {
    return gt(
      'Purchase order {number} is {status}. Matching waits until it is fully received.',
      {
        context: 'Order Not Ready badge tooltip for one purchase order',
        number: orderNumber,
        status,
      },
    );
  }
  if (orderNumber) {
    return gt(
      'Purchase order {number} is not ready to match yet. Matching waits until it is fully received.',
      {
        context:
          'Order Not Ready badge tooltip for one purchase order with no status',
        number: orderNumber,
      },
    );
  }
  if (status) {
    return gt(
      'This purchase order is {status}. Matching waits until it is fully received.',
      {
        context:
          'Order Not Ready badge tooltip when the purchase order number is unknown',
        status,
      },
    );
  }
  return gt(
    'This purchase order is not ready to match yet. Matching waits until it is fully received.',
    {
      context:
        'Order Not Ready badge tooltip when the purchase order number and status are unknown',
    },
  );
}

function salesOrderSentence(
  gt: Translate,
  orderNumber: string | null,
  status: string | null,
): string {
  if (orderNumber && status) {
    return gt(
      'Sales order {number} is in {status} status. Matching waits until it is invoiced.',
      {
        context: 'Order Not Ready badge tooltip for one sales order',
        number: orderNumber,
        status,
      },
    );
  }
  if (orderNumber) {
    return gt(
      'Sales order {number} is not ready to match yet. Matching waits until it is invoiced.',
      {
        context:
          'Order Not Ready badge tooltip for one sales order with no status',
        number: orderNumber,
      },
    );
  }
  if (status) {
    return gt(
      'This sales order is in {status} status. Matching waits until it is invoiced.',
      {
        context:
          'Order Not Ready badge tooltip when the sales order number is unknown',
        status,
      },
    );
  }
  return gt(
    'This sales order is not ready to match yet. Matching waits until it is invoiced.',
    {
      context:
        'Order Not Ready badge tooltip when the sales order number and status are unknown',
    },
  );
}

const PO_STATUS_CONTEXT = 'purchase order status on the Order Not Ready badge';
const SO_STATUS_CONTEXT =
  'Famous sales order status on the Order Not Ready badge';

function purchaseOrderStatusLabel(status: string, gt: Translate): string {
  const labels: Record<string, string> = {
    PENDING_CONFIRMATION: gt('Pending Confirmation', {
      context: PO_STATUS_CONTEXT,
    }),
    CONFIRMED: gt('Confirmed', { context: PO_STATUS_CONTEXT }),
    DRAFT: gt('Draft', { context: PO_STATUS_CONTEXT }),
    PENDING_APPROVAL: gt('Pending Approval', { context: PO_STATUS_CONTEXT }),
    APPROVED: gt('Approved', { context: PO_STATUS_CONTEXT }),
    SENT: gt('Sent', { context: PO_STATUS_CONTEXT }),
    PARTIALLY_RECEIVED: gt('Partially Received', {
      context: PO_STATUS_CONTEXT,
    }),
    FULLY_RECEIVED: gt('Fully Received', { context: PO_STATUS_CONTEXT }),
    PARTIALLY_INVOICED: gt('Partially Invoiced', {
      context: PO_STATUS_CONTEXT,
    }),
    FULLY_INVOICED: gt('Fully Invoiced', { context: PO_STATUS_CONTEXT }),
    CLOSED: gt('Closed', { context: PO_STATUS_CONTEXT }),
    CANCELLED: gt('Cancelled', { context: PO_STATUS_CONTEXT }),
  };
  return labels[status] ?? status;
}

function salesOrderStatusLabel(status: string, gt: Translate): string {
  const labels: Record<string, string> = {
    '0': gt('Imported', { context: SO_STATUS_CONTEXT }),
    '1': gt('Quote', { context: SO_STATUS_CONTEXT }),
    '2': gt('Order', { context: SO_STATUS_CONTEXT }),
    '4': gt('Ship', { context: SO_STATUS_CONTEXT }),
    '6': gt('Invoice', { context: SO_STATUS_CONTEXT }),
    D: gt('Deleted', { context: SO_STATUS_CONTEXT }),
    P: gt('Partial Receipt', { context: SO_STATUS_CONTEXT }),
    C: gt('Paid In Full', { context: SO_STATUS_CONTEXT }),
  };
  return labels[status.toUpperCase()] ?? status;
}
